import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search,
  Printer,
  Receipt,
  Loader2,
  FileSearch,
  ChevronLeft,
  ChevronRight,
  UserRound,
  Wallet,
  X,
  RotateCw,
  Undo2,
  Hourglass,
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatusPill } from '@/components/pos/StatusPill';
import { MoneyValue } from '@/components/pos/MoneyValue';
import { EmptyState } from '@/components/pos/EmptyState';
import { ReceiptPreview } from '@/components/pos/ReceiptPreview';
import { ResumePaymentDialog } from '@/components/pos/ResumePaymentDialog';
import { DatePickerField } from '@/components/pos/DatePickerField';
import { ReprintReasonDialog } from '@/components/pos/ReprintReasonDialog';
import { GroupClaimPanel } from '@/components/pos/GroupClaimPanel';
import { VoidRefundRequestDialog } from '@/components/pos/VoidRefundRequestDialog';
import {
  activeTickets,
  channelKey,
  claimButtonLabel,
  claimOf,
  isPartnerBooking,
  refusalText,
} from '@/lib/groupClaim';
import { useMyApprovalRequests, useOrderLookup } from '@/hooks/queries';
import { PERM, useCan } from '@/lib/permissions';
import { kindLabel, requestMatchesSale } from '@/lib/approvals';
import {
  useSaleDetail,
  usePrintBatch,
  usePrintSingle,
  useReprintBatch,
  useReprintWithNewQr,
} from '@/hooks/mutations';
import type { ReprintInput } from '@/api/qrCode.api';
import { useDeviceStore } from '@/stores/device.store';
import { useHotkeys } from '@/hooks/useHotkeys';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { formatDateTime, todayISO } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ticketLabel } from '@/lib/ticketLabel';
import { printRefusalMessage, summarizePrintBatch } from '@/lib/printRefusalErrors';
import { hasKey, useT } from '@/lib/i18n';
import type { ApprovalKind, SaleApprovalRequest, SaleDetail, SaleLookupRow } from '@/types/sale';

const PER_PAGE = 8;

const PAYMENT_LABEL: Record<string, string> = {
  cash: 'Cash',
  // The tender means "online payment" — Midtrans, Xendit or Doku took it.
  midtrans: 'Online',
  'e-money': 'E-Money',
  emoney: 'E-Money',
  qris: 'QRIS',
};
const CHANNEL_LABEL: Record<string, string> = {
  ticketbox: 'Ticketbox',
  online: 'Online',
  kiosk: 'Kiosk',
  travel_agent: 'Travel Agent',
  manless: 'Manless',
  // AFB-S14: a partner sale's channel derived from the partner (helper/partnerChannel)
  ota: 'OTA',
  affiliator: 'Affiliator',
  affiliator_hotel: 'Affiliator · Hotel',
};

function printChipClass(s?: string | null): string {
  switch (s) {
    case 'PRINTED':
      return 'bg-green-100 text-green-700';
    case 'PRINTING':
      return 'bg-amber-100 text-amber-700';
    case 'FAILED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-600'; // PENDING / unknown
  }
}

function paymentChipClass(g?: string | null): string {
  switch ((g || '').toLowerCase()) {
    case 'cash':
      return 'bg-green-100 text-green-700';
    case 'midtrans':
      return 'bg-blue-100 text-blue-700';
    case 'e-money':
    case 'emoney':
      return 'bg-emerald-100 text-emerald-700';
    case 'qris':
      return 'bg-violet-100 text-violet-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

const CHIP_BASE = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium';

export function OrderLookupPage() {
  // Seed the search from ?q= (e.g. a recent order tapped on Beranda), so the
  // invoice lands pre-filtered and ready to reprint.
  const [searchParams] = useSearchParams();
  const { locale, t } = useT();
  // #1853: a print-status / partner-channel chip, translated when we have a key.
  const printStatusLabel = (s: string) => {
    const key = `printStatus.${s}`;
    return hasKey(key) ? t(key) : s;
  };
  const channelLabel = (row: SaleLookupRow) => {
    const k = channelKey(row);
    const key = `channel.${k}`;
    return hasKey(key) ? t(key) : (CHANNEL_LABEL[k] ?? String(row.channel ?? row.posType));
  };
  const approvalKindLabel = (kind: string) => {
    const key = `approvalKind.${kind}`;
    return hasKey(key) ? t(key) : kindLabel(kind);
  };
  const initialQ = searchParams.get('q') ?? '';
  const [term, setTerm] = useState(initialQ);
  const [activeQuery, setActiveQuery] = useState(initialQ);
  // Empty = all dates; pick a day to narrow.
  const [date, setDate] = useState('');
  // 'own' = this operator's orders (default); 'all' = every cashier's.
  const [scope, setScope] = useState<'own' | 'all'>('own');
  const [page, setPage] = useState(1);
  const printerBound = useDeviceStore((s) => Boolean(s.ticketPrinterId));

  // The list shows the operator's orders (or all with the toggle); `date`
  // narrows to a day, `q` refines by invoice / name / phone.
  const { data: lookup, isFetching, refetch: refetchOrders } = useOrderLookup({
    q: activeQuery,
    date,
    page,
    perPage: PER_PAGE,
    scope,
  });
  const rows = lookup?.rows ?? [];
  const total = lookup?.total ?? 0;
  const totalPage = lookup?.totalPage ?? 1;

  const { mutate: fetchDetail, isPending: detailLoading } = useSaleDetail();
  const { mutate: printBatch, isPending: printingBatch } = usePrintBatch();
  const { mutate: printOne } = usePrintSingle();
  const { mutate: reprintBatch, isPending: reprintingBatch } = useReprintBatch();
  const { mutateAsync: reprintOne } = useReprintWithNewQr();
  const [reprintingIds, setReprintingIds] = useState(false);
  const printing = printingBatch || reprintingBatch || reprintingIds;

  const [selected, setSelected] = useState<SaleDetail | null>(null);
  // ABT-S22: a partner group (found by its INV-… / GRP-…) claims its
  // wristbands once, the whole group together (no partial claim).
  const partner = isPartnerBooking(selected);
  const summary = claimOf(selected);
  const claimed = Boolean(summary?.groups.some((g) => g.claim));
  // What the reason dialog will reprint: the whole order, or one ticket group.
  const [reprintTarget, setReprintTarget] = useState<
    { kind: 'all'; count: number } | { kind: 'ids'; ids: string[]; label: string } | null
  >(null);

  // ── Void / refund requests (approved by a supervisor in the BO) ──────────
  const canVoid = useCan(PERM.VOID_REQUEST);
  const canRefund = useCan(PERM.REFUND_REQUEST);
  const canRequest = canVoid || canRefund;
  // The operator's own open requests — drives the "Menunggu persetujuan"
  // badge. Polled so a supervisor's decision clears it without a reload.
  const { data: pendingList, dataUpdatedAt: pendingUpdatedAt } = useMyApprovalRequests(
    { status: 'PENDING', perPage: 100 },
    { enabled: canRequest, refetchInterval: 10_000 },
  );
  // Sales marked pending locally (a just-sent request or a 409 "already
  // pending") until the polled list catches up — a mark older than the last
  // list refresh is dropped, so a rejected request frees the buttons again.
  const [localPendingMarks, setLocalPendingMarks] = useState<
    Record<string, { kind: ApprovalKind | 'PENDING'; at: number }>
  >({});
  const localPending = useMemo(() => {
    const out: Record<string, ApprovalKind | 'PENDING'> = {};
    for (const [inv, m] of Object.entries(localPendingMarks)) {
      if (m.at > pendingUpdatedAt) out[inv] = m.kind;
    }
    return out;
  }, [localPendingMarks, pendingUpdatedAt]);
  const markPending = (invoiceId: string, kind: ApprovalKind | 'PENDING') =>
    setLocalPendingMarks((m) => ({ ...m, [invoiceId]: { kind, at: Date.now() } }));
  const [requestKind, setRequestKind] = useState<ApprovalKind | null>(null);
  const pendingFor = (sale: { id?: string | null; invoiceId: string }): SaleApprovalRequest | null =>
    pendingList?.rows.find((r) => r.status === 'PENDING' && requestMatchesSale(r, sale)) ?? null;
  const selectedPending = selected ? pendingFor(selected) : null;
  const selectedLocalPending = selected ? localPending[selected.invoiceId] : undefined;
  const showRequestActions =
    canRequest && Boolean(selected) && String(selected?.status) === 'SUCCESS' && !partner;

  // Any filter change returns to the first page.
  useEffect(() => {
    setPage(1);
  }, [activeQuery, date, scope]);

  const run = (q: string) => {
    const trimmed = q.trim();
    // Mirror the searched value into the box too, so a scan that fires while
    // the input isn't focused still shows what was looked up.
    setTerm(trimmed);
    setActiveQuery(trimmed);
  };

  // Clear the search box AND the active query so the list drops back to the
  // full (filtered-by-date/scope) view.
  const clearSearch = () => {
    setTerm('');
    setActiveQuery('');
  };

  const openDetail = (row: SaleLookupRow) => {
    fetchDetail(row.invoiceId, { onSuccess: (d) => setSelected(d) });
  };

  // Print-once: a ticket with printCounts 0 takes a normal print; one already
  // printed can only be REPRINTED, which mints a new QR and needs a reason.
  // A voided wristband (a cancelled member) is neither printed nor counted.
  const tickets = activeTickets(selected?.tickets);
  const unprintedCount = tickets.filter((t) => !(t.printCounts ?? 0)).length;
  const printedCount = tickets.length - unprintedCount;

  // Re-read the order so the print / reprint split reflects what just happened.
  const refreshSelected = () => {
    void refetchOrders();
    if (selected) fetchDetail(selected.invoiceId, { onSuccess: (d) => setSelected(d) });
  };

  const canPrint = () => {
    if (!selected) return false;
    if (!printerBound) {
      toast.warning(t('orders.toast.printerNotSet'));
      return false;
    }
    if (['CANCELLED', 'REFUND'].includes(String(selected.status))) {
      toast.error(t('orders.toast.cancelledNoPrint'));
      return false;
    }
    return true;
  };

  /** Main action: print what has never been printed, else offer a reprint. */
  const printOrReprint = () => {
    if (!selected || !canPrint()) return;
    if (partner && summary) {
      if (!summary.claimable && (!claimed || unprintedCount > 0)) {
        toast.error(
          summary.refusal ? refusalText(summary.refusal, summary, locale) : t('orders.toast.groupNotClaimable'),
        );
        return;
      }
    }
    if (unprintedCount === 0 || (partner && summary && !summary.claimable)) {
      setReprintTarget({ kind: 'all', count: printedCount });
      return;
    }
    printBatch(selected.invoiceId, {
      onSuccess: (res) => {
        // "N gagal" with no reason was the whole complaint in #1746: a refused
        // ticket (cancelled/refunded, or another visit day) is not a failure to
        // retry, so it is reported separately and BY REASON — a mixed invoice
        // can hold more than one. The counts keep the translated copy; the
        // reasons are server-authored sentences and pass through as they come.
        const s = summarizePrintBatch(res.tickets);
        for (const reason of s.refusalReasons) toast.error(reason, { duration: 12_000 });
        if (s.failed) toast.error(t('orders.toast.printedSomeFailed', { ok: s.printed, failed: s.failed }));
        else if (s.printed) toast.success(t('orders.toast.printed', { ok: s.printed }));
        else if (!s.refused) toast.warning(t('orders.toast.printedNone'));
        refreshSelected();
      },
      onError: (err) => toast.error(printRefusalMessage(err) ?? err.message),
    });
  };

  const confirmReprint = async (input: ReprintInput) => {
    if (!selected || !reprintTarget) return;
    if (reprintTarget.kind === 'all') {
      reprintBatch(
        { invoiceId: selected.invoiceId, ...input },
        {
          onSuccess: (res) => {
            const ok = res.tickets.filter((t) => t.status === 'PRINTED').length;
            const failed = res.tickets.filter((t) => t.status === 'FAILED');
            if (failed.length)
              toast.error(
                t('orders.toast.reprintedSomeFailed', { ok, failed: failed.length, message: failed[0]?.message ?? '' }),
              );
            else toast.success(t('orders.toast.reprinted', { ok }));
            setReprintTarget(null);
            refreshSelected();
          },
          onError: (err) => toast.error(err.message),
        },
      );
      return;
    }
    // One ticket group — sequential, so one cashier's reprints never race each
    // other at the printer.
    const { ids, label } = reprintTarget;
    setReprintingIds(true);
    let ok = 0;
    try {
      for (const qrId of ids) {
        try {
          await reprintOne({ qrId, ...input });
          ok += 1;
        } catch (err) {
          toast.error((err as Error).message);
        }
      }
    } finally {
      setReprintingIds(false);
    }
    if (ok) toast.success(t('orders.toast.reprintedGroup', { label, ok }));
    setReprintTarget(null);
    refreshSelected();
  };

  useHotkeys({ 'ctrl+p': printOrReprint });
  // Auto-search on scan even without a trailing Enter: `flushOnIdleMs` fires the
  // lookup once the scan burst goes quiet (human typing never triggers it).
  useBarcodeScanner({ onScan: (code) => run(code), flushOnIdleMs: 120 });

  // An order still awaiting payment can be finished from here. INITIAL is
  // included deliberately: a Snap sale starts INITIAL, so excluding it would
  // hide exactly the sales most likely to have been abandoned mid-payment.
  // Only midtrans can be continued from here. A QRIS/Snap charge lives at the
  // gateway until it expires, so re-opening it re-shows the SAME code and
  // moves no money — e-money would have to re-arm a reader and EDC has its own
  // resolve flow. The server refuses those anyway; not offering the button is
  // just not promising something it will then decline.
  const unpaid =
    Boolean(selected) &&
    ['PENDING', 'INITIAL'].includes(String(selected?.status)) &&
    String(selected?.paymentGateway) === 'midtrans';
  const [resumeOpen, setResumeOpen] = useState(false);

  const printDisabled =
    !selected ||
    !printerBound ||
    tickets.length === 0 ||
    ['CANCELLED', 'REFUND'].includes(String(selected.status)) ||
    (partner && !!summary && !summary.claimable && !claimed);

  // Collapse identical tickets (same product + visitor type) into one reprint
  // control so N adult tickets show as a single "×N" button instead of N
  // look-alike rows. The button reprints every ticket in the group.
  const ticketGroups = useMemo(() => {
    // Printed and never-printed tickets are separate groups: one prints, the
    // other reprints with a new QR, and a single button cannot mean both.
    const groups: Array<{ key: string; label: string; ids: string[]; printed: boolean }> = [];
    const byKey = new Map<string, number>();
    for (const t of activeTickets(selected?.tickets)) {
      // A group-booking band is named for its member, so each is its own
      // button — they are not look-alikes. See lib/ticketLabel.
      const label = ticketLabel(t);
      const printed = (t.printCounts ?? 0) > 0;
      const key = `${label}|${t.visitorType ?? ''}|${printed ? 'p' : 'n'}`;
      const at = byKey.get(key);
      if (at == null) {
        byKey.set(key, groups.length);
        groups.push({ key, label, ids: [t.id], printed });
      } else {
        groups[at]!.ids.push(t.id);
      }
    }
    return groups;
  }, [selected]);

  // Client-side receipt print to a thermal printer. The `printing-receipt`
  // class scopes the print CSS (80mm roll, monochrome — see index.css) to just
  // the receipt; cleared on afterprint (with a timeout fallback). Prints
  // SILENTLY (no dialog) when Chrome runs with --kiosk-printing and the thermal
  // printer is the default; otherwise the normal print dialog opens.
  const printReceipt = () => {
    if (!selected) return;
    const root = document.documentElement;
    root.classList.add('printing-receipt');
    const cleanup = () => {
      root.classList.remove('printing-receipt');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  };

  const printGroup = (g: { ids: string[]; label: string; printed: boolean }) => {
    if (!canPrint()) return;
    if (g.printed) {
      setReprintTarget({ kind: 'ids', ids: g.ids, label: g.label });
      return;
    }
    let done = 0;
    let settled = 0;
    g.ids.forEach((id) =>
      printOne(id, {
        onSuccess: () => {
          done += 1;
        },
        onError: (err) => {
          // A refusal says WHY (#1746) and stays up long enough to read it.
          const refusal = printRefusalMessage(err);
          if (refusal) toast.error(refusal, { id: 'print-refusal', duration: 12_000 });
          else toast.error(err.message);
        },
        onSettled: () => {
          settled += 1;
          if (settled === g.ids.length) {
            if (done) toast.success(t('orders.toast.groupPrinted', { label: g.label, count: done }));
            refreshSelected();
          }
        },
      }),
    );
  };

  return (
    <div className="grid h-full grid-cols-1 gap-6 px-6 py-6 lg:grid-cols-2">
      {/* Search + results */}
      <div className="flex min-h-0 flex-col">
        <h1 className="mb-3 font-display text-xl font-bold text-ink-900">{t('orders.title')}</h1>
        <div className="space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-400" />
              <Input
                data-scanner-target
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && run(term)}
                placeholder={t('orders.searchPlaceholder')}
                className="pl-12 pr-11"
                autoFocus
              />
              {term && (
                <button
                  type="button"
                  onClick={clearSearch}
                  aria-label={t('orders.clearSearch')}
                  className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-ink-400 transition-colors hover:bg-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button onClick={() => run(term)}>{t('orders.search')}</Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-ink-400">{t('orders.show')}</span>
            <Tabs value={scope} onValueChange={(v) => setScope(v as 'own' | 'all')}>
              <TabsList>
                <TabsTrigger value="own">{t('orders.scopeOwn')}</TabsTrigger>
                <TabsTrigger value="all">{t('orders.scopeAll')}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-ink-400">{t('orders.date')}</span>
            <DatePickerField
              value={date}
              onChange={setDate}
              max={todayISO()}
              placeholder={t('orders.allDates')}
              className="h-9"
            />
            {date && (
              <button
                type="button"
                onClick={() => setDate('')}
                className="text-xs font-medium text-primary hover:underline"
              >
                {t('orders.clearFilter')}
              </button>
            )}
            {total > 0 && (
              <span className="ml-auto text-xs text-ink-400">{t(total === 1 ? 'orders.countOne' : 'orders.count', { count: total })}</span>
            )}
          </div>
        </div>

        <ScrollArea className="mt-4 flex-1">
          {isFetching ? (
            <div className="flex justify-center py-12 text-ink-400">
              <Loader2 className="h-7 w-7 animate-spin" />
            </div>
          ) : activeQuery && rows.length === 0 ? (
            <EmptyState
              icon={FileSearch}
              title={t('orders.notFound')}
              description={t('orders.notFoundDesc', { q: activeQuery })}
            />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={FileSearch}
              title={t('orders.empty')}
              description={
                date ? t('orders.emptyDate') : scope === 'all' ? t('orders.emptyAll') : t('orders.emptyOwn')
              }
            />
          ) : (
            <ul className="space-y-2 pr-2">
              {rows.map((row) => (
                <li key={row.invoiceId}>
                  <button
                    type="button"
                    onClick={() => openDetail(row)}
                    className={cn(
                      'flex w-full items-center justify-between gap-3 rounded-lg border-2 bg-card px-4 py-3 text-left transition-colors hover:border-primary/40',
                      selected?.invoiceId === row.invoiceId ? 'border-primary' : 'border-border',
                    )}
                  >
                    <div className="min-w-0">
                      <p className="tnum truncate font-display font-semibold text-ink-900">
                        {row.invoiceId}
                      </p>
                      <p className="truncate text-xs text-ink-400">
                        {row.isPartnerBooking
                          ? t('orders.partnerRow', {
                              bookings: row.bookingNumbers?.join(', ') ?? '',
                              date: row.visitDate ?? '—',
                              headcount: row.headcount ?? 0,
                            })
                          : `${row.userInfo?.name ?? '—'} · ${formatDateTime(row.createdAt, locale)}`}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1">
                        {row.paymentGateway && (
                          <span className={cn(CHIP_BASE, paymentChipClass(row.paymentGateway))}>
                            {PAYMENT_LABEL[String(row.paymentGateway).toLowerCase()] ??
                              String(row.paymentGateway)}
                          </span>
                        )}
                        {(row.channel || row.posType) && (
                          <span className={cn(CHIP_BASE, 'bg-amber-100 text-amber-700')}>
                            {channelLabel(row)}
                          </span>
                        )}
                        {row.isPartnerBooking && row.partnerName && (
                          <span className={cn(CHIP_BASE, 'bg-sky-100 text-sky-700')}>{row.partnerName}</span>
                        )}
                        {row.cashierName && (
                          <span className={cn(CHIP_BASE, 'bg-slate-100 text-slate-600')}>
                            <UserRound className="h-2.5 w-2.5" />
                            {row.cashierName}
                          </span>
                        )}
                        {canRequest &&
                          (localPending[row.invoiceId] ||
                            pendingList?.rows.some(
                              (r) => r.status === 'PENDING' && requestMatchesSale(r, { invoiceId: row.invoiceId }),
                            )) && (
                            <span className={cn(CHIP_BASE, 'bg-amber-100 text-amber-700')}>
                              <Hourglass className="h-2.5 w-2.5" />
                              {t('orders.pendingApproval')}
                            </span>
                          )}
                        {row.printStatus && (
                          <span className={cn(CHIP_BASE, printChipClass(row.printStatus))}>
                            <Printer className="h-2.5 w-2.5" />
                            {printStatusLabel(row.printStatus)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {/* A partner group's price is the agent's cost: not shown (ABT-S22 AC9). */}
                      {!row.isPartnerBooking && <MoneyValue value={row.totalAmount} className="text-sm" />}
                      <StatusPill status={row.status} />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>

        {totalPage > 1 && (
          <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-4 w-4" /> {t('orders.prev')}
            </Button>
            <span className="text-xs text-ink-400">
              {t('orders.pageOf', { page, total: totalPage })}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              disabled={page >= totalPage || isFetching}
              onClick={() => setPage((p) => Math.min(totalPage, p + 1))}
            >
              {t('orders.next')} <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Detail + reprint */}
      <div className="flex min-h-0 flex-col">
        <h2 className="mb-3 font-display text-xl font-bold text-ink-900">{t('orders.detail')}</h2>
        <Card className="flex min-h-0 flex-1 flex-col">
          <CardContent className="flex min-h-0 flex-1 flex-col pt-5">
            {detailLoading ? (
              <div className="flex flex-1 items-center justify-center text-ink-400">
                <Loader2 className="h-7 w-7 animate-spin" />
              </div>
            ) : !selected ? (
              <EmptyState
                title={t('orders.noneSelected')}
                description={t('orders.noneSelectedDesc')}
                className="flex-1 border-0 bg-transparent"
              />
            ) : (
              <>
                <ScrollArea className="flex-1">
                  {partner && summary ? (
                    <GroupClaimPanel summary={summary} />
                  ) : (
                    <ReceiptPreview detail={selected} />
                  )}
                </ScrollArea>
                {/* Print-only clone — the browser prints just this subtree
                    (see the .printing-receipt rules in index.css). None for a
                    partner group: no priced receipt (ABT-S22 AC9). */}
                {!partner && (
                  <div id="receipt-print" aria-hidden="true" className="hidden">
                    <ReceiptPreview detail={selected} />
                  </div>
                )}
                <Separator className="my-4" />
                {unpaid && (
                  // Sits ABOVE the print actions on purpose: the first thing to
                  // do with an unpaid order is take the money. Printing is
                  // pointless until then — the tickets are only minted on
                  // settlement.
                  <Button
                    size="lg"
                    className="mb-2 h-14 w-full gap-2 text-lg"
                    onClick={() => setResumeOpen(true)}
                  >
                    <Wallet className="h-5 w-5" />
                    {t('orders.resumePayment')}
                  </Button>
                )}
                <div className="flex gap-2">
                  <Button
                    size="lg"
                    variant={unpaid ? 'outline' : 'default'}
                    className="h-14 flex-1 gap-2 text-lg"
                    onClick={printOrReprint}
                    disabled={printDisabled || printing}
                  >
                    {printing ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : unprintedCount === 0 ? (
                      <RotateCw className="h-5 w-5" />
                    ) : (
                      <Printer className="h-5 w-5" />
                    )}
                    {partner && summary?.claimable
                      ? claimButtonLabel(summary, locale)
                      : unprintedCount === 0
                        ? t('orders.reprintTickets')
                        : printedCount > 0
                          ? t('orders.printUnprinted', { count: unprintedCount })
                          : t('orders.printTickets')}
                    <kbd className="ml-1 rounded border border-primary-foreground/30 px-1.5 text-xs">
                      Ctrl+P
                    </kbd>
                  </Button>
                  {!partner && (
                    <Button
                      size="lg"
                      variant="outline"
                      className="h-14 gap-2 text-base"
                      onClick={printReceipt}
                    >
                      <Receipt className="h-5 w-5" />
                      {t('orders.printReceipt')}
                    </Button>
                  )}
                </div>
                {ticketGroups.length > 0 && (!partner || claimed) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {ticketGroups.map((g) => (
                      <Button
                        key={g.key}
                        variant="outline"
                        size="sm"
                        disabled={!printerBound || printing}
                        onClick={() => printGroup(g)}
                        title={t(g.printed ? 'orders.groupReprintTitle' : 'orders.groupPrintTitle')}
                      >
                        {g.printed ? (
                          <RotateCw className="mr-1 h-3.5 w-3.5" />
                        ) : (
                          <Printer className="mr-1 h-3.5 w-3.5" />
                        )}
                        {g.label}
                        {g.ids.length > 1 && (
                          <span className="tnum ml-1 text-ink-400">×{g.ids.length}</span>
                        )}
                      </Button>
                    ))}
                  </div>
                )}
                {showRequestActions &&
                  (selectedPending || selectedLocalPending ? (
                    <div
                      className="mt-3 flex items-center gap-2 rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800"
                      data-testid="approval-pending-badge"
                    >
                      <Hourglass className="h-4 w-4 shrink-0" />
                      {t('orders.awaitingSupervisor')}
                      {selectedPending?.kind
                        ? ` · ${approvalKindLabel(selectedPending.kind)}`
                        : selectedLocalPending && selectedLocalPending !== 'PENDING'
                          ? ` · ${approvalKindLabel(selectedLocalPending)}`
                          : ''}
                    </div>
                  ) : (
                    <div className="mt-3 flex gap-2">
                      {canVoid && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 gap-1.5"
                          onClick={() => setRequestKind('VOID')}
                        >
                          <Undo2 className="h-3.5 w-3.5" /> {t('orders.requestVoid')}
                        </Button>
                      )}
                      {canRefund && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 gap-1.5"
                          onClick={() => setRequestKind('FULL_REFUND')}
                        >
                          <Undo2 className="h-3.5 w-3.5" /> {t('orders.requestRefund')}
                        </Button>
                      )}
                    </div>
                  ))}
              </>
            )}
          </CardContent>
        </Card>
      </div>
      <ReprintReasonDialog
        open={reprintTarget !== null}
        onOpenChange={(o) => !o && setReprintTarget(null)}
        count={reprintTarget ? (reprintTarget.kind === 'all' ? reprintTarget.count : reprintTarget.ids.length) : 0}
        submitting={printing}
        onConfirm={confirmReprint}
      />
      {selected && showRequestActions && (
        <VoidRefundRequestDialog
          open={requestKind !== null}
          onOpenChange={(o) => !o && setRequestKind(null)}
          sale={{ id: selected.id, invoiceId: selected.invoiceId, totalAmount: selected.totalAmount }}
          canVoid={canVoid}
          canRefund={canRefund}
          initialKind={requestKind ?? undefined}
          onSubmitted={(row) => markPending(selected.invoiceId, row?.kind ?? 'PENDING')}
          onPendingConflict={() => markPending(selected.invoiceId, 'PENDING')}
        />
      )}
    {selected && (
        <ResumePaymentDialog
          open={resumeOpen}
          onOpenChange={setResumeOpen}
          invoiceId={selected.invoiceId}
          total={selected.totalAmount}
          onPaid={(inv) => {
            // The sale now has tickets: refresh the list so the row reads
            // SUCCESS, and reload the detail the print buttons act on.
            void refetchOrders();
            fetchDetail(inv, { onSuccess: (d) => setSelected(d) });
            toast.success(t('orders.toast.paymentReceived'));
          }}
        />
      )}
    </div>
  );
}
